import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  RemoteConfigStore,
  requestErrorMessageFactory,
  SOFTLINE_FEATURE_REMOTE_CONFIG } from '@softline/application';
import { UserInfoService } from '@softline/auth';
import { SoftAppsUserInfo } from '@softapps/core';
import {
  ApiResourceLocation,
  DateTimeTzService,
  endOfMonth,
  Message,
  SOFTLINE_SERVICE_HTTP,
  startOfYear,
  Store,
} from '@softline/core';
import { BehaviorSubject, combineLatestWith, filter, firstValueFrom, map } from 'rxjs';
import { MainChartComponent } from '../../components/main-chart/main-chart.component';
import { UmsatzstatistikService } from '../../services/umsatzstatistik-service';
import { WwsUmsatzstatistikAufgliederungStore } from '../../store/aufgliederung.store';
import { Umsatzstatistik } from '../../types/umsatzstatistik';
import { UmsatzstatistikQuery } from '../../types/umsatzstatistik.query';
import { SOFTLINE_API_WWS_UMSATZABFRAGE_VKFORG } from '../../umsatzstatistik.api';
import { SOFTLINE_FEATURE_WWS_UMSATZSTATISTIK_AUFGLIEDERUNG } from '../../umsatzstatistik.shared';
import { extractUmsatzstatistikRemoteConfig } from '../../utils/extract-remote-config';
import { toChartData } from '../../utils/to-chart-data';

import {
  CardBodyComponent,
  CardComponent,
  CardDescriptionComponent,
  CardHeaderComponent,
  CardLeadingComponent,
  CardTitleComponent,
  EmptyStateComponent,
  FormatPipe,
  IconComponent,
  InterpolatePipe,
  SOFTLINE_DATA_CONTEXT,
} from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-umsatzstatistik-widget',
  imports: [CommonModule, MainChartComponent,
    FormatPipe,
    TranslatePipe,
    InterpolatePipe,
    EmptyStateComponent,
    CardComponent,
    CardHeaderComponent,
    CardLeadingComponent,
    CardTitleComponent,
    CardDescriptionComponent,
    CardBodyComponent,
    IconComponent],
  templateUrl: './umsatzstatistik-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './umsatzstatistik-widget.component.css',
})
export class UmsatzstatistikWidgetComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly service = inject(UmsatzstatistikService);
  private readonly httpService = inject(SOFTLINE_SERVICE_HTTP);
  private readonly dateTimeTzService = inject(DateTimeTzService);
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);

  private readonly data$ = new BehaviorSubject<Umsatzstatistik | null>(null);
  private readonly today = () => this.dateTimeTzService.now();

  public readonly kundennummer = input<string | null | undefined>(null);
  public readonly idktostamm = input<number | null>(null);
  // rendered through a widget outlet (CUSTOMER_DETAIL): the customer arrives as outlet context
  private readonly outletContext = inject(SOFTLINE_DATA_CONTEXT, { optional: true }) as {
    kundennummer?: string | null;
    idktostamm?: number | null;
  } | null;
  private readonly customerNumber = computed(
    () => this.kundennummer() ?? this.outletContext?.kundennummer ?? null
  );
  private readonly customerId = computed(
    () => this.idktostamm() ?? this.outletContext?.idktostamm ?? null
  );
  public readonly loading = signal(false);
  public readonly error = signal<Message<object> | null>(null);
  public readonly startDate = computed(() => {
    return startOfYear(this.today());
  });
  public readonly endDate = computed(() => endOfMonth(this.today()));

  public readonly noData$ = toObservable(this.loading).pipe(
    combineLatestWith(this.data$),
    map(([loading, data]) => !loading && (data === null || this.chartData()?.length < 1))
  );

  public readonly chartData = toSignal(
    this.data$.pipe(
      filter((o): o is Umsatzstatistik => !!o),
      map((o) => o.ebeneresult),
      combineLatestWith(
        this.store
          .observe(SOFTLINE_FEATURE_REMOTE_CONFIG, RemoteConfigStore.getters.data)
          .pipe(map((o) => extractUmsatzstatistikRemoteConfig(o)))
      ),
      map(([umsatzstatistik, remoteConfig]) => toChartData(umsatzstatistik, remoteConfig))
    ),
    { initialValue: [] }
  );

  async ngOnInit(): Promise<void> {
    try {
      this.loading.set(true);
      const remoteConfig = await this.store.dispatch(
        SOFTLINE_FEATURE_REMOTE_CONFIG,
        RemoteConfigStore.actions.load
      );
      const umsatzstatistikRemoteConfig =
        extractUmsatzstatistikRemoteConfig(remoteConfig);
      const aufgliederungen = await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_UMSATZSTATISTIK_AUFGLIEDERUNG,
        WwsUmsatzstatistikAufgliederungStore.actions.loadItems
      );
      const aufgliederung = aufgliederungen.find(
        (o) => o.umsatzaufgliederung === umsatzstatistikRemoteConfig?.defaultAufgliederung
      );
      const query = {
        ebene: {
          umsatzebene: umsatzstatistikRemoteConfig?.defaultEbene ?? 'Vkforg',
          key: 'VKFORG',
        },
        umsatzart: {
          umsatzart: umsatzstatistikRemoteConfig?.defaultUmsatzart ?? 'Gesamt',
        },
        aufgliederung: aufgliederung ?? null,
        vondatum: this.startDate(),
        bisdatum: this.endDate(),
        filiale: null,
        menge: 0,
        vergleich: false,
      };

      const result = await this.loadUmsatzstatistik(query);
      this.data$.next(result);
      this.loading.set(false);
    } catch (e) {
      this.loading.set(false);
      this.error.set(requestErrorMessageFactory(e as object));
    }
  }

  private async loadUmsatzstatistik(
    query: Partial<UmsatzstatistikQuery>
  ): Promise<Umsatzstatistik | null> {
    const accountId = this.userInfo.value()?.accountid;

    const location: ApiResourceLocation = {
      path: SOFTLINE_API_WWS_UMSATZABFRAGE_VKFORG,
      queryParams:
        (this.customerId() ?? accountId)
          ? { idktostamm: this.customerId() ?? accountId }
          : undefined,
    };
    const filialen = await firstValueFrom(this.httpService.get<any[]>(location));
    const requestBody = {
      ebene: query?.ebene?.umsatzebene,
      umsatzart: query?.umsatzart?.umsatzart,
      aufgliederung: query?.aufgliederung?.name,
      filialen: query?.filiale !== null ? [query.filiale] : filialen,
      vondat: query?.vondatum,
      bisdat: query?.bisdatum,
      menge: 0,
      vergleich: false,
    };
    if (this.customerNumber()) {
      requestBody['kunde'] = this.customerNumber();
    }
    return await firstValueFrom(this.service.loadUmsatzstatistik(requestBody));
  }
}
